import type { BulkVoucherCmd } from "@/data/commands/table/BulkVoucherCmd";
import {
    BaseServiceDao,
    type IEditableDataSource,
    type IPaginatedResult,
    parsePaginatedResult,
} from "@/data/dao/service/BaseServiceDao";
import type { IVoucher } from "@/data/model/Voucher";
import type { ICreateVoucher } from "@/data/queries/voucher/ICreateVoucherCmd";
import type { IUpdateVoucherCmd } from "@/data/queries/voucher/IUpdateVoucherCmd";
import type { IVoucherQry } from "@/data/queries/voucher/IVoucherQry";
import { CurrentLocationService } from "@/data/services/CurrentLocationService";
import { DateTime } from "@/data/util/DateTime";

export class VoucherDao extends BaseServiceDao implements IEditableDataSource<IVoucher> {
    public static className = "VoucherDao";

    constructor() {
        super("voucher");
        this.isLocationDependent = true;
    }

    public async getSingle(id: string): Promise<IVoucher> {
        return this.get(id, undefined, VoucherDao.parseVoucher);
    }

    public async getData(qry: IVoucherQry): Promise<IPaginatedResult<IVoucher>> {
        return this.get("", qry, (f) => parsePaginatedResult(f, VoucherDao.parseVoucher));
    }

    public async create(cmd: ICreateVoucher): Promise<IVoucher> {
        return this.post("", cmd, VoucherDao.parseVoucher);
    }

    public async update(id: string, cmd: IUpdateVoucherCmd): Promise<IVoucher> {
        return this.put(id, cmd, VoucherDao.parseVoucher);
    }

    public async cancel(id: string): Promise<IVoucher> {
        return this.put(id + "/cancel", {}, VoucherDao.parseVoucher);
    }

    public async remove(id: string): Promise<void> {
        return this.delete(id, {});
    }

    public async bulk(cmd: BulkVoucherCmd): Promise<void> {
        return this.post("bulk", cmd);
    }

    public async downloadQrCode(voucherCode: string) {
        const locationId = CurrentLocationService.getLocationId();
        location.href =
            this.basePath +
            "/" +
            voucherCode +
            "/qrcode?c=" +
            new Date().getTime() +
            (locationId !== undefined ? "&locationId=" + locationId : "");
    }

    public static parseVoucher(literal: any): IVoucher {
        return {
            balance: parseFloat(literal.balance),
            code: literal.code,
            createdDateTime: DateTime.parseIsoDate(literal.createdDateTime),
            expirationDateTime: DateTime.parseIsoDate(literal.expirationDateTime),
            initialValue: parseFloat(literal.initialValue),
            type: parseInt(literal.type),
            id: literal.id,
            internalNote: literal.internalNote == null ? undefined : literal.internalNote,
            locationId: literal.locationId == null ? undefined : literal.locationId,
            isCancelled: literal.isCancelled,
            issuedInPortal: literal.issuedInPortal,
            cancellationDateTime: literal.cancellationDateTime == null ? undefined : DateTime.parseIsoDate(literal.cancellationDateTime),
            state: parseInt(literal.state),
        };
    }
}
